// src/app/app-shell.tsx
// Authenticated shell: the persistent practitioner nav rail (composed from the DS
// PractitionerNav frame — graduated in scrips-signal-ds#86, shipped v1.9.0) + the
// top bar + the workspace frame around the routed outlet + the force-logout dialog
// (DS ModalYesNo).
//
// PROD-1104: the rail is no longer BLOCKED/hand-built — it composes the DS primitive.
// Items are DATA: until the backend menu-contents API (FetchMenuContentsById) is wired
// (PROD-1188), we pass the 4-item canonical set as a typed default. brandSlot is the Scrips
// wordmark (showcase, prototype fa3aebd 14 Oct: the ScripsBrandMark orb is kept for Scrips
// Intelligence; the wordmark is the brand, 20px tall in the 56px rail, in the app's brand blue). footerSlot (avatar + account menu) is composed inline (NavFooterSlot,
// PROD-1178/1186) — Samer-sanctioned until DS Userpic/Menu graduate (PROD-1121). The
// Notifications bell + inbox (PROD-1187) is composed into the bottom cluster with the avatar
// (host-composed, "SLOT, don't bake"); Help (PROD-1189) sits in the top bar (top-bar.tsx).
// Gap report: docs/ds-gaps/phase-0-shell.md · epic PROD-1184
//
// Phone-call drawer containment (PR #225 Codex follow-up): the dashboard's phone-call
// drawer (`src/dashboard/dashboard-view.tsx`) already marks its own header + worklist
// `inert` while open, but that page-level containment can't reach this rail — the rail
// is a sibling of the routed `<Outlet/>`, not a descendant of the page. We read
// `usePhoneCallDrawerStore` (a tiny shared store, mirroring `usePrintPreviewStore`) and
// mark ONLY the rail `inert` while the drawer is open — never the `<Outlet/>` wrapper,
// since the drawer itself renders inside the routed page and must stay interactive.
import { useState, useCallback, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  ModalYesNo,
  PractitionerNav,
  type PractitionerNavItem,
} from '@scripsteam/scrips-signal-ds';
import { useAuthStore } from '@/auth/auth-store';
import { useForceLogout } from '@/auth/use-force-logout';
import { usePhoneCallDrawerStore } from '@/dashboard/store/phone-call-drawer-store';
import { NavFooterSlot } from './nav-footer-slot';
import { TopBar } from './top-bar';
import { RailBrand } from './rail-brand';
import { IconButtonTooltips } from './icon-button-tooltips';
import { useRailExpanded } from './rail-state';
import { NotificationBell } from '@/notifications/components/notification-bell';
import { PrintPreviewDialog } from '@/billing/components/print-preview-dialog';
import { usePrintPreviewStore } from '@/billing/store/print-preview-store';
import { bumpPrintEpoch } from '@/billing/services/print-epoch';
import { useHomePath } from '@/shared/lib/home-path';
import { isTasksOn } from '@/tasks/lib/tasks-switch';
import { navItems } from '@/tasks/lib/tasks-nav';
import { StaffChatDock } from '@/staff-chat/components/staff-chat-dock';
import { StaffChatHubMount } from '@/staff-chat/components/staff-chat-hub-mount';
import { isTeamInRailOn, TEAM_NAV_ID, withTeam } from '@/staff-chat/lib/staff-chat-switch';
import { useDockStore } from '@/staff-chat/lib/dock-store';
import { TELEVISIT_LAYER_ID } from './televisit-layer';
import { isFrameOneFallbackOn } from '@/shared/lib/encounter-flags';
import { isAnalyticsOn, withAnalytics } from '@/analytics-hub/analytics-switch';

// Canonical nav set + order (Flutter menu_container_screen.dart:259–289). Interim
// typed default — replace with the backend menu-contents API in Phase 1. The rail
// itself bakes in nothing; these items are passed in as data.
// The design's three tiles (PRACTITIONER_NAV: Today · Calendar · Patients). Today is the home; `/dashboard` stays
// routable and is the same home concept, so it lights the Today tile (see `navSelectedId`).
// Icons (Today uses the design's `dashboard` glyph): the Practice app's rail icons (Antonin, 15 Oct: "take what is in PM and use it for PA"),
// through the DS Icon registry, which maps `dashboard` / `calendar` / `users` to the same Lucide
// LayoutDashboard / Calendar / Users the Practice side-nav draws. Today has no Practice counterpart, so it keeps
// main's own DS glyph (`nav-today`) rather than a newly chosen one.
export const NAV_ITEMS: PractitionerNavItem[] = [
  { id: 'today', label: 'Today', icon: 'dashboard' },
  { id: 'calendar', label: 'Calendar', icon: 'calendar' },
  { id: 'patients', label: 'Patients', icon: 'users' },
  // Account Settings is NOT a top tile — faithful to Flutter (top = 4 tiles); it lives in
  // the avatar account menu (NavFooterSlot). The /settings route still exists. (PROD-1186.)
];

// ── Bottom-cluster labeled tiles (Figma shell frame parity, PROD-1588 wave S2) ──────────
/** Rail tile lit for a path: `/dashboard` is the same home concept as `/today`. The encounter lights Today too (Antonin's export, 22 Oct:
 *  every encounter screen mounts through `encounterFrame`, Today lit, Today › patient): a visit is opened from the day's list. */
export function navSelectedId(pathname: string): string {
  const seg = pathname.split('/')[1] || 'today';
  return seg === 'dashboard' || seg === 'encounter' ? 'today' : seg;
}

export function AppShellFrame() {
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useAuthStore((s) => s.logout);
  const [forcedOut, setForcedOut] = useState(false);
  // Stable identity: AppShell re-renders on every navigation (selectedId derives
  // from useLocation), and useForceLogout has this callback in its effect deps —
  // an inline arrow would tear down + rebuild the SignalR Identity hub each nav.
  const handleForceLogout = useCallback(() => setForcedOut(true), []);
  useForceLogout(handleForceLogout);
  const phoneCallDrawerOpen = usePhoneCallDrawerStore((s) => s.open);
  // Folded (56px, icons) or with labels (232px): remembered, `[` toggles, labels by default from
  // 2200px wide (rail-state.ts).
  const [railExpanded, toggleRail] = useRailExpanded();

  // PHI isolation: the print-preview store is a module-global singleton, so a
  // resolved invoice/receipt PDF (SAS link OR a same-origin blob) survives this
  // shell. On logout the shell unmounts; if it remounts for a DIFFERENT user in
  // the same SPA the stale PDF would be visible. Clear + revoke on unmount so no
  // preview state (and no PHI object URL) crosses an auth boundary. (PROD-1001
  // review, P1.) Also bump the print epoch — a safety net independent of the
  // login/logout identity subscription in `print-epoch.ts`, so an in-flight
  // print request cannot write into the store after this shell instance is
  // gone for ANY reason, not only a session change. (Codex v5 P1/P2.)
  useEffect(
    () => () => {
      usePrintPreviewStore.getState().close();
      bumpPrintEpoch();
    },
    [],
  );

  // Selected tile = first path segment (/today → 'today'); default to Today
  // (source default = contents[1]). The encounter lights Today (navSelectedId); other non-nav routes select nothing.
  const selectedId = navSelectedId(location.pathname);
  const teamDockOpen = useDockStore((s) => s.open);
  const openTeamDock = useDockStore((s) => s.openDock);
  const collapseTeamDock = useDockStore((s) => s.collapse);
  const toggleTeamDock = () => (teamDockOpen ? collapseTeamDock() : openTeamDock());
  // The Today tile and the logo open home: the ready list at /dashboard, for everyone (home-path.ts).
  const homePath = useHomePath();
  // Rehearsal testers must not stay on Frame 1 by accident (2 Nov): the dev fallback shows a marker. Read again on every URL
  // change, so a `?x=-frame-1` opened from inside the app clears it.
  const frameOne = useMemo(() => isFrameOneFallbackOn(), [location.search]);

  return (
    // Pin the shell to the viewport so the rail (incl. its bottom avatar/account
    // menu) stays fixed and content scrolls inside the frame — otherwise a tall
    // page (e.g. the encounter IPS with hundreds of uncapped flags) stretches the rail
    // to the full document height and pushes the footer avatar off-screen. (PROD-1185.)
    // `h-app-screen` (`--app-vh`, not 100vh) because the app runs under a root `zoom` density
    // scale, and viewport units don't compensate for it — the contract lives in the Signal DS
    // `app.css`. Falls back to 100vh wherever that sheet isn't loaded (jsdom tests). This outer
    // box stays a plain block so the dialogs below never become flex items.
    //
    // Showcase shell (zone contract Z1-Z3): composed here instead of the DS AppShell, which has
    // no top-bar slot. A row on the app background: the rail, then a column of the 48px top bar
    // over the white workspace frame. The frame `<main>` is the only scroller, as the DS `<main>`
    // was. Looks: src/styles/showcase-bridge.css under `/* shell */`.
    <div className="h-app-screen overflow-hidden">
      <div className="shell-root flex h-full w-full overflow-hidden">
        {/* `inert` while the phone-call drawer is open (see file header comment) —
            a plain wrapper rather than a prop on `PractitionerNav`, because the DS
            component's prop-forwarding to its root element isn't a guaranteed contract
            (same rationale as dashboard-view.tsx). Only the rail is wrapped, never the
            frame — the drawer itself renders inside the routed page and must stay
            interactive. `shell-rail` re-scopes the rail colours (bridge CSS). */}
        <div className="shell-rail" data-rail={railExpanded ? 'expanded' : 'collapsed'} inert={phoneCallDrawerOpen}>
          <PractitionerNav
            items={withTeam(withAnalytics(navItems(NAV_ITEMS, isTasksOn()), isAnalyticsOn()), isTeamInRailOn())}
            selectedId={selectedId}
            // `?x=team-in-rail`: the Team tile opens (or closes) the chat panel; it is not a page.
            onSelect={(id: string) =>
              id === TEAM_NAV_ID ? toggleTeamDock() : navigate(id === 'today' ? homePath : `/${id}`)
            }
            ariaLabel="Practitioner"
            brandSlot={
              <RailBrand expanded={railExpanded} onToggle={toggleRail} onHome={() => navigate(homePath)} />
            }
            footerSlot={
              // Bottom cluster (top→bottom): Notifications bell (PROD-1187, opens the inbox
              // panel) · the avatar account trigger. Help (PROD-1189) moved up to the top bar
              // (design log 12 Oct). Every tile is the DS `RailAction`; gap-4 (16px) is the rail's
              // own tile pitch (48px tile + 16px), so the cluster reads like the tiles above.
              //
              // Both pass `hideCaption`: the footer cluster is GLYPH-ONLY (Ahmed,
              // 2026-09-04). The captions stay on the nav tiles above, which are destinations.
              // `label` survives as the accessible name on both.
              <div className="flex flex-col items-center gap-4">
                <NotificationBell />
                <NavFooterSlot />
              </div>
            }
          />
        </div>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <TopBar />
          {frameOne && (
            <div role="status" className="border border-status-warning bg-status-warning-bg px-3 py-1 text-xs text-text-primary">
              Frame 1 (dev fallback) is on in this browser. Open any page with <code>?x=-frame-1</code> to return to frame v2.
            </div>
          )}
          {/* No `contextRail` at the app frame: the right rail is PER-SURFACE by contract (the
              encounter mounts its own ContextRail-framed drawer inside the routed page). */}
          <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
            <main className="shell-frame min-h-0 min-w-0 flex-1 overflow-y-auto">
              <Outlet />
            </main>
            {/* The encounter video panel's layer (televisit-layer.ts): over the frame's box, not
                scrolled with it, as Flutter's FloatingVideoWidget sits in the encounter Stack. */}
            <div
              id={TELEVISIT_LAYER_ID}
              className="televisit-layer pointer-events-none absolute left-0 top-0"
            />
          </div>
        </div>
      </div>
      <ModalYesNo
        open={forcedOut}
        title="Session ended"
        message="Your session was ended on another device. Please sign in again."
        confirmLabel="Sign in"
        cancelLabel="Dismiss"
        onConfirm={logout}
        onCancel={() => setForcedOut(false)}
      />
      {/* Single mounted reader for usePrintPreviewStore — the invoice/receipt
          print mutations resolve a PDF and push it into the store; this shows it
          in the in-app preview (Flutter PrintInvoiceState/PrintReceiptState),
          not a discarded browser tab. (PROD-1001.) */}
      <PrintPreviewDialog />
      {/* Staff chat live delivery (SR-14): connects to the Notifications hub only with `?x=staff-chat` on. */}
      <StaffChatHubMount />
      {/* Staff chat (SR-14, `?x=staff-chat`): the Team dock, bottom right of every page, left of the Intelligence rail
          when one is on screen. Mounted once here; nothing when off. */}
      <StaffChatDock />
      {/* One instant tooltip for every icon-only button (16 Oct): reads the button's accessible name. */}
      <IconButtonTooltips />
    </div>
  );
}
